import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as Logo} from 'images/cs-logo.svg';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTransferTree from 'components/inputs/FormikTransferTree';
import {layoutActions, layoutSelectors} from 'features/layout/redux';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import Panel from 'components/Panel';
import {useValues} from 'common/hooks/useValues';
import Formik from 'components/hook-form/Formik';
import {SupportTicketNames, SupportTicketRequest} from 'types/diplomat/SupportTicketRequest';
import Loader from 'components/Loader';
import {useFilteredTransactions} from 'common/hooks/useFilteredTransactions';
import {useIsPermittedByRole} from 'common/hooks/useIsPermittedByRole';
import {AdminResourceType} from 'types/diplomat/settings/adminroles/AdminResourceType';

type SupportTicketProps = {close: () => void};

const useStyles = makeStyles((theme) => ({
   root: {
      padding: theme.spacing(2),
   },
   pushedRight: {
      textAlign: 'right',
   },
   centered: {
      textAlign: 'center',
   },
   mailLink: {
      color: 'blue',
      textDecorationLine: 'underline',
   },
}));

const SupportTicketForm = ({close}: SupportTicketProps) => {
   const {t} = useTranslation();
   const classes = useStyles();

   const folders = useSelector(layoutSelectors.transactionSyncFoldersSelector);

   const filter = useValues('_filter') as string;
   const filtered = useFilteredTransactions(filter);

   const isGenerating = useSelector(layoutSelectors.isSupportTicketGenerating);
   const isPermittedByRole = useIsPermittedByRole();

   const isLogsPermitted = isPermittedByRole(AdminResourceType.enum.LOGS);
   const isConfigPermitted = isPermittedByRole(AdminResourceType.enum.BACKUP_RESTORE);

   return (
      <>
         <Grid className={classes.root} container spacing={2}>
            <Grid item xs={9}>
               <Typography gutterBottom>
                  <Trans>
                     Provide a meaningful description of the issue you're encountering, select the
                     details to include with any relevant Transactions, and click SEND. The
                     information will be captured in a zip file that is automatically downloaded by
                     your browser. Then create a new email to{' '}
                     <a className={classes.mailLink} href="mailto:support@coviantsoftware.com">
                        support@coviantsoftware.com
                     </a>{' '}
                     with a descriptive subject line, and attach the downloaded zip file before
                     sending.
                  </Trans>
               </Typography>
            </Grid>
            <Grid className={classes.pushedRight} item xs={3}>
               <Logo />
            </Grid>

            <Grid item xs={12}>
               <FormikTextField
                  multiline
                  rows={4}
                  maxRows={4}
                  name={SupportTicketNames.description}
                  label={t('Description')}
               />
            </Grid>
            <Grid item title={isLogsPermitted ? '' : t('Insufficient Privileges to enable')}>
               <FormikCheckbox
                  name={SupportTicketNames.includeLogs}
                  label={t('Include recent log files')}
                  disabled={!isLogsPermitted}
               />
            </Grid>
            <Grid item title={isConfigPermitted ? '' : t('Insufficient Privileges to enable')}>
               <FormikCheckbox
                  name={SupportTicketNames.includeConfig}
                  label={t('Include configuration')}
                  disabled={!isConfigPermitted}
               />
            </Grid>
            <Grid item xs={12}>
               <FormikTransferTree
                  name={SupportTicketNames.transactions}
                  options={filtered}
                  folders={folders}
                  leftLabel={t('Available Transactions')}
                  rightLabel={t('Selected Transactions')}
               />
            </Grid>
            <Grid container justifyContent="center">
               <Grid item md={6} sm={8}>
                  <Panel name={t('Filter Criteria')}>
                     <Typography>
                        {t(
                           'Enter a phrase to display only Transaction names containing the filter phrase in the Available Transactions panel.'
                        )}
                     </Typography>
                     <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={10}>
                           <FormikTextField name="_filter" label={t('Filter Phrase')} />
                        </Grid>
                        {/* <Grid item><Button>{t('')}</Button></Grid>
                     <Grid item><Button>{t('')}</Button></Grid> */}
                     </Grid>
                  </Panel>
               </Grid>
            </Grid>
            {isGenerating && (
               <Grid container justifyContent="center">
                  <Loader />
               </Grid>
            )}
            <FormFooterButtons submitLabel={t('Save')} cancel={close} />
            {/* <Grid className={classes.centered} item xs={12}>
               <Button onClick={close}>
                  <Trans>Ok</Trans>
               </Button>
            </Grid> */}
         </Grid>
      </>
   );
};

type Props = {
   onClose: () => void;
};
export const SupportTicketDialog = ({onClose}: Props) => {
   const dispatch = useDispatch();

   const isPermittedByRole = useIsPermittedByRole();

   const isLogsPermitted = isPermittedByRole(AdminResourceType.enum.LOGS);
   const isConfigPermitted = isPermittedByRole(AdminResourceType.enum.BACKUP_RESTORE);

   return (
      <Dialog maxWidth="md" header={DialogTypes.SupportTicket.dialogName} close={onClose}>
         <Formik
            initialValues={SupportTicketRequest.cast({
               [SupportTicketNames.transactions]: [],
               [SupportTicketNames.includeLogs]: isLogsPermitted,
               [SupportTicketNames.includeConfig]: isConfigPermitted,
            })}
            onSubmit={(values, helpers) => {
               dispatch(layoutActions.sendSupportTicket(values));
            }}
         >
            <SupportTicketForm close={onClose} />
         </Formik>
      </Dialog>
   );
};

export default React.memo(SupportTicketDialog);
